import { Button } from '@/components/ui/button';
import { Calendar, Clock, X } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export default function BookingsList({ bookings, rooms, currentUserId, onCancel }) {
  const getRoomName = (roomId) => {
    const room = rooms.find(r => r.id === roomId);
    return room?.name || 'Sala desconhecida';
  };

  const formatDateTime = (dateString) => {
    try {
      const date = parseISO(dateString);
      return format(date, "dd MMM yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return dateString;
    }
  };

  const formatTime = (dateString) => {
    try {
      const date = parseISO(dateString);
      return format(date, 'HH:mm', { locale: ptBR });
    } catch {
      return dateString;
    }
  };

  const sortedBookings = [...bookings].sort((a, b) => 
    new Date(a.start_time) - new Date(b.start_time)
  );

  return (
    <section>
      <div className="mb-8">
        <h3 className="text-2xl font-manrope font-bold tracking-tight mb-1">
          Próximas Reuniões
        </h3>
        <p className="text-muted-foreground font-inter">
          {bookings.length} {bookings.length === 1 ? 'reunião agendada' : 'reuniões agendadas'}
        </p>
      </div>

      {bookings.length === 0 ? (
        <div className="text-center py-16 px-4">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <Calendar className="w-8 h-8 text-muted-foreground" />
          </div>
          <h4 className="text-lg font-manrope font-semibold mb-2">Nenhuma reunião agendada</h4>
          <p className="text-muted-foreground font-inter">Reserve uma sala para começar</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {sortedBookings.map((booking) => {
            const isOwnBooking = booking.user_id === currentUserId;
            const isPast = new Date(booking.end_time) < new Date();
            
            return (
              <div
                key={booking.id}
                data-testid={`booking-card-${booking.id}`}
                className={`rounded-xl border p-6 transition-all hover:shadow-md ${
                  isPast
                    ? 'bg-muted/30 border-border/30 opacity-60'
                    : 'bg-white border-border/50'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Calendar className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-manrope font-semibold text-lg mb-1">
                          {booking.meeting_name}
                        </h4>
                        <p className="text-sm text-muted-foreground font-inter mb-2">
                          Responsável: <span className="font-medium text-foreground">{booking.responsible}</span>
                        </p>
                        <div className="flex flex-wrap items-center gap-4 text-sm">
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <div className="w-2 h-2 rounded-full bg-secondary" />
                            <span className="font-medium text-foreground">{getRoomName(booking.room_id)}</span>
                          </div>
                          <div className="flex items-center gap-2 text-muted-foreground font-mono">
                            <Clock className="w-4 h-4" />
                            <span>{formatTime(booking.start_time)} - {formatTime(booking.end_time)}</span>
                          </div>
                          <div className="text-muted-foreground font-mono">
                            {formatDateTime(booking.start_time).split(' às ')[0]}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {isOwnBooking && !isPast && (
                    <Button
                      data-testid={`cancel-booking-button-${booking.id}`}
                      onClick={() => onCancel(booking.id)}
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10 rounded-full"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Cancelar
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}